import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import ProfileImageUpload from "@/components/ProfileImageUpload";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Shield, Mail, Calendar, Edit2, Save, X } from "lucide-react";

const AdminProfile = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    phone: "",
    location: ""
  });

  useEffect(() => {
    // Check if user is admin
    if (!user?.isAdmin) {
      toast.error("Bu sayfaya erişim yetkiniz yok");
      navigate("/");
      return;
    }

    if (user) {
      setProfileData({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        location: user.location || ""
      });
    }
  }, [user]);

  const handleImageUpdate = async (imageData) => {
    try {
      await axios.put(`${API}/users/profile`, {
        profileImage: imageData
      });
      toast.success("Profil resmi güncellendi");
      window.location.reload();
    } catch (error) {
      console.error("Image update error:", error);
      throw error;
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/users/profile`, profileData);
      toast.success("Profil güncellendi");
      setIsEditing(false);
      window.location.reload();
    } catch (error) {
      console.error("Profile update error:", error);
      toast.error("Profil güncellenemedi");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("tr-TR", { 
      year: "numeric", 
      month: "long", 
      day: "numeric" 
    });
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-violet-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-violet-50">
      <Navbar user={user} onLogout={onLogout} />

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent flex items-center gap-3">
              <Shield className="h-8 w-8 text-purple-600" />
              Admin Profili
            </h1>
            <p className="text-base text-gray-600">Platform yöneticisi profil ayarları</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Profile Image Card */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-lg">Profil Resmi</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center">
                <ProfileImageUpload 
                  currentImage={user.profileImage}
                  userName={user.name}
                  onImageUpdate={handleImageUpdate}
                />
                <p className="text-sm text-gray-500 mt-4 text-center">
                  Resminizi değiştirmek için tıklayın
                </p>
              </CardContent>
            </Card>

            {/* Profile Info Card */}
            <Card className="lg:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Profil Bilgileri</CardTitle>
                {!isEditing ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                  >
                    <Edit2 className="h-4 w-4 mr-2" />
                    Düzenle
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditing(false)}
                    >
                      <X className="h-4 w-4 mr-2" />
                      İptal
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSave}
                      disabled={loading}
                      className="bg-gradient-to-r from-purple-500 to-violet-600"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Kaydet
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">İsim</Label>
                  <Input
                    id="name"
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    disabled={!isEditing}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">E-posta (Admin)</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-purple-600" />
                    <Input
                      id="email"
                      value={profileData.email}
                      disabled
                      className="pl-10 bg-purple-50 border-purple-200"
                    />
                  </div>
                  <p className="text-xs text-gray-500">Admin email değiştirilemez</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Telefon</Label>
                  <Input
                    id="phone"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    disabled={!isEditing}
                    placeholder="0555 555 55 55"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Konum</Label>
                  <Input
                    id="location"
                    value={profileData.location}
                    onChange={(e) => setProfileData({ ...profileData, location: e.target.value })}
                    disabled={!isEditing}
                    placeholder="İl, İlçe"
                  />
                </div>

                <div className="pt-4 border-t">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="h-4 w-4" />
                    <span>Kayıt Tarihi: {formatDate(user.createdAt)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 mt-2">
                    <Shield className="h-4 w-4 text-purple-600" />
                    <span className="font-semibold text-purple-600">Platform Yöneticisi</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-lg">Hızlı İşlemler</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button
                  variant="outline"
                  className="h-20 flex flex-col items-center justify-center"
                  onClick={() => navigate("/admin")}
                >
                  <Shield className="h-6 w-6 mb-2 text-purple-600" />
                  <span>Admin Paneli</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-20 flex flex-col items-center justify-center"
                  onClick={() => {
                    // Navigate to users tab
                    navigate("/admin");
                  }}
                >
                  <Mail className="h-6 w-6 mb-2 text-blue-600" />
                  <span>Kullanıcıları Görüntüle</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-20 flex flex-col items-center justify-center text-red-600 hover:bg-red-50"
                  onClick={onLogout}
                >
                  <X className="h-6 w-6 mb-2" />
                  <span>Çıkış Yap</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminProfile;
